interface ExchangeRates {
  [key: string]: number;
}

interface ExchangeRateResponse {
  rates: ExchangeRates;
  base: string;
  date: string;
}

class ExchangeRateService {
  private cache: Map<string, { rates: ExchangeRates; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 1000 * 60 * 60; // 1 hour cache
  private readonly BASE_URL = 'https://api.exchangerate-api.com/v4/latest';

  async getExchangeRates(baseCurrency: string = 'CAD'): Promise<ExchangeRates> {
    const cacheKey = baseCurrency;
    const cached = this.cache.get(cacheKey);
    
    // Check if we have valid cached data
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.rates;
    }

    try {
      const response = await fetch(`${this.BASE_URL}/${baseCurrency}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch exchange rates: ${response.status}`);
      }
      
      const data: ExchangeRateResponse = await response.json();
      
      // Cache the results
      this.cache.set(cacheKey, {
        rates: data.rates,
        timestamp: Date.now()
      });
      
      return data.rates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      
      // Return fallback rates if API fails
      return this.getFallbackRates(baseCurrency);
    }
  }

  async convertCurrency(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    try {
      const rates = await this.getExchangeRates(fromCurrency);
      const rate = rates[toCurrency];
      
      if (!rate) {
        throw new Error(`Exchange rate not found for ${toCurrency}`);
      }
      
      return amount * rate;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount; // Return original amount if conversion fails
    }
  }

  private getFallbackRates(baseCurrency: string): ExchangeRates {
    // Fallback rates (approximate) if API fails
    const fallbackRates: { [key: string]: ExchangeRates } = {
      'CAD': {
        'USD': 0.74,
        'EUR': 0.68,
        'GBP': 0.58,
        'JPY': 110.0,
        'AUD': 1.12,
        'CHF': 0.65,
        'CNY': 5.35,
        'CAD': 1.0
      },
      'USD': {
        'CAD': 1.35,
        'EUR': 0.92,
        'GBP': 0.78,
        'JPY': 148.0,
        'AUD': 1.51,
        'CHF': 0.88,
        'CNY': 7.23,
        'USD': 1.0
      },
      'EUR': {
        'CAD': 1.47,
        'USD': 1.09,
        'GBP': 0.85,
        'JPY': 161.0,
        'AUD': 1.64,
        'CHF': 0.96,
        'CNY': 7.86,
        'EUR': 1.0
      }
    };

    return fallbackRates[baseCurrency] || fallbackRates['CAD'];
  }

  // Get supported currencies
  getSupportedCurrencies(): string[] {
    return ['CAD', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CHF', 'CNY'];
  }

  // Get currency symbol
  getCurrencySymbol(currency: string): string {
    const symbols: { [key: string]: string } = {
      'CAD': 'C$',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥'
    };
    return symbols[currency] || currency;
  }
}

export const exchangeRateService = new ExchangeRateService();
export default exchangeRateService; 